import { FC, useCallback, useState } from 'react';
import { CircularProgress } from '@mui/material';

import {
	BillIcon,
	CheckmarkCircleIcon,
	CrossCircleIcon,
	CurrentlocationIcon,
	MplListItem,
} from '@mopla/ui';

type TInvoiceItemState = 'idle' | 'loading' | 'success' | 'error';

/** How long the success/error icon stays before the row resets to idle. */
const RESET_DELAY_MS = 2000;

interface IInvoiceOptionItemProps {
	label: string;
	/** Sends the invoice e-mail(s); resolves on success, rejects on failure. */
	onSend: () => Promise<void>;
	/** Called with the thrown error so the parent can show the error sheet. */
	onError: (error: unknown) => void;
	dataTestid?: string;
}

/**
 * Invoice option row on the ticket detail. Keeps the standard option-row look
 * (bill icon + label) but the right icon reflects the send state:
 * mail → spinner → checkmark / cross, then resets. Mirrors the D-Ticket
 * download-invoices row; the error sheet is owned by the parent.
 */
export const InvoiceOptionItem: FC<IInvoiceOptionItemProps> = ({
	label,
	onSend,
	onError,
	dataTestid,
}) => {
	const [state, setState] = useState<TInvoiceItemState>('idle');

	const handleClick = useCallback(async () => {
		if (state !== 'idle') {
			return;
		}
		setState('loading');
		try {
			await onSend();
			setState('success');
		} catch (error) {
			setState('error');
			onError(error);
		} finally {
			setTimeout(() => setState('idle'), RESET_DELAY_MS);
		}
	}, [state, onSend, onError]);

	const rightIcon =
		state === 'loading' ? (
			<CircularProgress size={24} color="primary" />
		) : state === 'success' ? (
			<CheckmarkCircleIcon />
		) : state === 'error' ? (
			<CrossCircleIcon />
		) : (
			<CurrentlocationIcon />
		);

	return (
		<MplListItem
			text={label}
			leftIcon={<BillIcon />}
			rightIcon={rightIcon}
			onClick={handleClick}
			disabled={state !== 'idle'}
			dataTestid={dataTestid}
		/>
	);
};
